/**
 * useExtensionPresence — detecta presença + capabilities e guarda o fluxo no captureStore.
 * Montado no AppShell. Não ativa a captura local-first; o rollout de captureTarget é separado.
 */
import { useCallback, useEffect } from 'react';
import {
  getExtensionFlow,
  reconcileExtensionObservation,
  type ExtensionConnection,
  type ExtensionFlow,
} from '../lib/extensionFlow';
import {
  attemptAutomaticExtensionReload,
  completeExtensionReconnect,
} from '../lib/extensionReconnect';
import { useCaptureStore } from '../stores/captureStore';
import { useUiStore } from '../stores/uiStore';
import { EXTENSION_RECONNECT_MODAL_ID } from './useRequireExtension';

export function useExtensionPresence(watch = true): {
  present: boolean | null;
  flow: ExtensionFlow | null;
  connection: ExtensionConnection;
  recheck: () => Promise<boolean>;
} {
  const present = useCaptureStore((s) => s.extensionPresent);
  const flow = useCaptureStore((s) => s.extensionFlow);
  const connection = useCaptureStore((s) => s.extensionConnection);
  const setObservation = useCaptureStore((s) => s.setExtensionObservation);
  const openModal = useUiStore((s) => s.openModal);

  const recheck = useCallback(async (): Promise<boolean> => {
    const previousFlow = useCaptureStore.getState().extensionFlow;
    const detectedFlow = await getExtensionFlow();
    const observation = reconcileExtensionObservation(previousFlow, detectedFlow);
    setObservation(observation);

    if (observation.connection === 'connected') {
      const pendingWindow = completeExtensionReconnect();
      if (pendingWindow) useCaptureStore.getState().openCaptureWindow(pendingWindow);
      return true;
    }

    if (observation.connection === 'refresh-required') {
      const reloading = attemptAutomaticExtensionReload(useCaptureStore.getState());
      if (!reloading) openModal(EXTENSION_RECONNECT_MODAL_ID);
    }
    return false;
  }, [setObservation, openModal]);

  useEffect(() => {
    if (!watch) return;
    void recheck();
    const recheckLiveCode = () => void recheck();
    window.addEventListener('focus', recheckLiveCode);
    window.addEventListener('pageshow', recheckLiveCode);
    return () => {
      window.removeEventListener('focus', recheckLiveCode);
      window.removeEventListener('pageshow', recheckLiveCode);
    };
  }, [recheck, watch]);

  return { present, flow, connection, recheck };
}
